import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type Quill from 'quill/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import useAppSelector from '../app/redux/useAppSelector';
import type { Boundary } from './types';
import { useUIElementResizeListener } from './UIElementResizeContext';
import useEditorWidthChange from './useEditorWidthChange';
import getNestedTargetForRemoteSelection from './utils/getNestedTargetForRemoteSelection';
import type Mark from './utils/Mark';
import styles from './Cursor.module.scss';

type Position = {
  bounds: Boundary;
  quill: Quill;
};

interface CursorNameLabelProps {
  user: { index: number; name: string };
  shrunken: boolean;
  position: Position;
  quill: Quill;
}

const CursorNameLabelPortal = ({
  user,
  shrunken,
  position,
  quill,
}: CursorNameLabelProps) => {
  const { left, top, height } = position.bounds;
  const offset = useMemo(() => {
    if (shrunken) return null;
    if (position.quill === quill) return { left, top: top + height };
    const rect = position.quill.container.getBoundingClientRect();
    const rootRect = quill.container.getBoundingClientRect();
    return {
      left: left + rect.left - rootRect.left,
      top: top + rect.top - rootRect.top + height,
    };
  }, [shrunken, left, top, height, position.quill, quill]);

  return createPortal(
    <AnimatePresence>
      {offset && (
        <motion.div
          initial={false}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className={classNames(
            styles.nameLabel,
            styles[`color${user.index + 1}`],
          )}
          style={{
            left: offset.left,
            top: offset.top,
          }}
        >
          {user.name}
        </motion.div>
      )}
    </AnimatePresence>,
    // Render the label in root quill so it can overflow the container editor.
    // We don't render the label in document.body to make it scroll with the editor.
    quill.container,
  );
};

const shrinkAfterMs = 3000;

interface CursorProps {
  mark: Mark;
  quill: Quill;
  user: { index: number; name: string };
  shrink?: boolean;
}

const Cursor = ({ mark, quill, user, shrink = true }: CursorProps) => {
  const [isShrunken, setIsShrunken] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const version = useAppSelector((state) => state.editor.version);

  useEffect(() => {
    if (!shrink) return undefined;
    const timeout = setTimeout(() => {
      setIsShrunken(true);
    }, shrinkAfterMs);
    return () => clearTimeout(timeout);
  }, [shrink]);

  const updatePosition = useCallback(() => {
    getNestedTargetForRemoteSelection(quill, mark).then((target) => {
      if (target) {
        const bounds = target.quill.getBounds(target.range.index);
        if (bounds) {
          setPosition({
            bounds,
            quill: target.quill,
          });
          return;
        }
      }

      setPosition(null);
    });
  }, [quill, mark]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition, version]);

  const addUIElementResizeListener = useUIElementResizeListener();
  useEffect(() => {
    return addUIElementResizeListener(updatePosition);
  }, [updatePosition, addUIElementResizeListener]);

  useEditorWidthChange(position?.quill, updatePosition);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [isCursorVisible, setIsCursorVisible] = useState(false);
  useEffect(() => {
    if (!container || isShrunken) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        setIsCursorVisible(entries?.[0]?.isIntersecting === true);
      },
      { threshold: [1] },
    );

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [container, isShrunken]);

  return (
    position && (
      <>
        {createPortal(
          <div className={styles.container}>
            <div
              ref={setContainer}
              className={classNames(
                styles.cursor,
                styles[`color${user.index + 1}`],
              )}
              style={{
                left: position.bounds.left,
                height: position.bounds.height,
                top: position.bounds.top,
              }}
            />
          </div>,
          position.quill.container,
        )}
        {isCursorVisible && (
          <CursorNameLabelPortal
            user={user}
            shrunken={isShrunken}
            position={position}
            quill={quill}
          />
        )}
      </>
    )
  );
};

export default Cursor;
